import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon, 
  ArrowPathIcon 
} from '@heroicons/react/24/outline';
import { reportService } from '../../services/reportService';
import { Button, Card, DatePicker, Loading } from '../common';
import { formatCurrency, formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';

const NetPositionReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const startStr = dateRange.startDate.toISOString().split('T')[0];
      const endStr = dateRange.endDate.toISOString().split('T')[0];
      
      const response = await reportService.getNetPositions(startStr, endStr);
      if (response.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Error fetching net positions:', error);
      toast.error(t('reports.errors.fetchFailed') || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, [dateRange]);

  const calculateNetColor = (amount) => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-gray-900';
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.startDate') || 'Start Date'}
            </label>
            <DatePicker
              selected={dateRange.startDate}
              onChange={date => setDateRange(prev => ({ ...prev, startDate: date }))}
              maxDate={new Date()}
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.endDate') || 'End Date'}
            </label>
            <DatePicker
              selected={dateRange.endDate}
              onChange={date => setDateRange(prev => ({ ...prev, endDate: date }))}
              maxDate={new Date()}
              minDate={dateRange.startDate}
            />
          </div>
          <div className="w-full sm:w-auto">
            <Button onClick={fetchPositions} disabled={loading}>
              <ArrowPathIcon className={`h-5 w-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {t('common.refresh') || 'Refresh'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Results Table */}
      <Card>
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('reports.netPositions') || 'Daily Position Analysis'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('reports.netPositionsSubtitle') || 'Analyze your currency flow to decide what to buy or sell.'}
          </p>
        </div>
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-12 flex justify-center">
              <Loading />
            </div>
          ) : data.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              {t('common.noData') || 'No data available for this period.'}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.currency') || 'Currency'}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('reports.opening') || 'Opening'}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider text-green-700 bg-green-50">
                    {t('reports.bought') || 'Bought'} (In)
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider text-red-700 bg-red-50">
                    {t('reports.sold') || 'Sold'} (Out)
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('reports.netChange') || 'Net Change'}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('reports.closing') || 'Closing'}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('reports.avgBuyRate') || 'Avg Buy Rate'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((row) => (
                  <tr key={row.currency} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {row.currency}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {formatCurrency(row.opening, row.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium text-right bg-green-50/50">
                      {formatCurrency(row.bought, row.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium text-right bg-red-50/50">
                      {formatCurrency(row.sold, row.currency)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold text-right ${calculateNetColor(row.netChange)}`}>
                      <div className="flex items-center justify-end gap-1">
                        {row.netChange > 0 ? (
                          <ArrowTrendingUpIcon className="h-4 w-4" />
                        ) : row.netChange < 0 ? (
                          <ArrowTrendingDownIcon className="h-4 w-4" />
                        ) : null}
                        {formatCurrency(Math.abs(row.netChange), row.currency)}
                        {row.netChange > 0 ? '+' : row.netChange < 0 ? '-' : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium text-right">
                      {formatCurrency(row.closing, row.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {row.avgBuyRate > 0 ? formatCurrency(row.avgBuyRate, '', 0) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default NetPositionReport;
